import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import { calcLeakRate, type ApplianceType } from '../../util/leak-rate.js'
import {
    buildRefrigerantLogPdf,
    daysBetween,
    type RefrigerantLogData,
} from '../../util/refrigerant-log.js'
import { exportPdf } from '../../util/pdf.js'

const REFRIGERANT_TYPES = [
    'R-22',
    'R-410A',
    'R-404A',
    'R-134a',
    'R-407C',
    'R-448A',
    'R-449A',
    'R-507A',
    'R-717',
    'Other',
]

const APPLIANCE_LABELS:Record<ApplianceType, string> = {
    commercial: 'Commercial Refrigeration',
    industrial: 'Industrial Process Refrigeration',
    comfort: 'Comfort Cooling',
    other: 'Other',
}

// Company
const companyName = signal('')
const technicianName = signal('')
const epaCertNumber = signal('')
const branch = signal('')
const phone = signal('')
const email = signal('')

// Customer/Site
const customerName = signal('')
const siteName = signal('')
const address = signal('')
const equipmentLocation = signal('')
const equipmentId = signal('')

// Refrigerant
const refrigerantType = signal('R-410A')
const addedLbs = signal('')
const recoveredLbs = signal('')
const fullChargeLbs = signal('')

// Leak Rate
const dateCurrentService = signal('')
const datePreviousAddition = signal('')
const applianceType = signal<ApplianceType>('commercial')

// Service Notes
const leakLocation = signal('')
const repairsPerformed = signal('')
const componentsReplaced = signal('')
const followUp = signal('')
const additionalNotes = signal('')

const submitted = signal(false)

const autoLeakRate = computed(() => {
    const added = parseFloat(addedLbs.value)
    const charge = parseFloat(fullChargeLbs.value)
    const dc = dateCurrentService.value
    const dp = datePreviousAddition.value
    if (!added || !charge || !dc || !dp || charge <= 0) return null
    const days = daysBetween(dc, dp)
    return calcLeakRate({
        applianceType: applianceType.value,
        refrigerantAdded: added,
        fullSystemCharge: charge,
        daysSinceLast: days,
    })
})

function handleSubmit (e:Event):void {
    e.preventDefault()
    submitted.value = true
}

function handleReset (e:Event):void {
    e.preventDefault()
    batch(() => {
        companyName.value = ''
        technicianName.value = ''
        epaCertNumber.value = ''
        branch.value = ''
        phone.value = ''
        email.value = ''
        customerName.value = ''
        siteName.value = ''
        address.value = ''
        equipmentLocation.value = ''
        equipmentId.value = ''
        refrigerantType.value = 'R-410A'
        addedLbs.value = ''
        recoveredLbs.value = ''
        fullChargeLbs.value = ''
        dateCurrentService.value = ''
        datePreviousAddition.value = ''
        applianceType.value = 'commercial'
        leakLocation.value = ''
        repairsPerformed.value = ''
        componentsReplaced.value = ''
        followUp.value = ''
        additionalNotes.value = ''
        submitted.value = false
    })
}

function handleDownloadPdf (e:Event):void {
    e.preventDefault()
    const added = parseFloat(addedLbs.value)
    const charge = parseFloat(fullChargeLbs.value)
    if (!technicianName.value || !companyName.value || !customerName.value) {
        return
    }
    if (!added || !charge || !dateCurrentService.value ||
        !datePreviousAddition.value) {
        return
    }

    const data:RefrigerantLogData = {
        companyName: companyName.value,
        technicianName: technicianName.value,
        epaCertNumber: epaCertNumber.value || undefined,
        branch: branch.value || undefined,
        phone: phone.value || undefined,
        email: email.value || undefined,
        customerName: customerName.value,
        siteName: siteName.value || undefined,
        address: address.value || undefined,
        equipmentLocation: equipmentLocation.value || undefined,
        equipmentId: equipmentId.value || undefined,
        refrigerantType: refrigerantType.value,
        addedLbs: added,
        recoveredLbs: recoveredLbs.value ?
            parseFloat(recoveredLbs.value) : undefined,
        fullChargeLbs: charge,
        dateCurrentService: dateCurrentService.value,
        datePreviousAddition: datePreviousAddition.value,
        applianceType: applianceType.value,
        leakLocation: leakLocation.value || undefined,
        repairsPerformed: repairsPerformed.value || undefined,
        componentsReplaced: componentsReplaced.value || undefined,
        followUp: followUp.value || undefined,
        additionalNotes: additionalNotes.value || undefined,
    }

    exportPdf({
        title: 'Refrigerant Service Log',
        sections: buildRefrigerantLogPdf(data),
        footer: 'For educational and operational planning purposes only. ' +
            'Not legal advice. Does not predict enforcement outcomes.',
        filename: `refrigerant-service-log-${dateCurrentService.value}.pdf`,
    })
}

export const RefrigerantLogGenerator:FunctionComponent<{
    state:AppState
}> = function RefrigerantLogGenerator () {
    const lr = autoLeakRate.value

    const record = (lr || submitted.value) && html`
        <div class="tool-readout" data-status=${
            lr && lr.exceedsThreshold !== null ?
                (lr.exceedsThreshold ? 'exceeds' : 'compliant') :
                'neutral'
        }>
            ${lr ?
                html`
                    <p class="tool-readout-label">Annualized leak rate</p>
                    <p class="tool-readout-value">
                        ${lr.leakRatePercent.toFixed(1)}<span
                            class="tool-readout-unit"
                        >%</span>
                    </p>
                    ${lr.exceedsThreshold !== null ?
                        html`<p class="tool-status" data-status=${
                            lr.exceedsThreshold ? 'exceeds' : 'compliant'
                        }>
                            ${lr.exceedsThreshold ?
                                `Exceeds the ${lr.threshold}% limit` :
                                `Within the ${lr.threshold}% limit`}
                        </p>` :
                        null
                    }
                ` :
                html`<p class="tool-readout-detail">
                    Add the refrigerant amounts and both service dates to
                    preview the annualized leak rate.
                </p>`
            }
            ${submitted.value ?
                html`<div class="tool-actions">
                    <substrate-button
                        type="button"
                        onClick=${handleDownloadPdf}
                    >
                        Download PDF Log
                    </substrate-button>
                </div>` :
                null
            }
        </div>
    `

    return html`
        <${ToolPage}
            title="Refrigerant Log Generator"
            description="Fill out the service-log form and download a
                print-ready EPA-compliant PDF record with an auto-calculated
                leak rate."
        >
            <${ToolWorkbench}
                inputsLabel="Service details"
                resultsLabel="Record"
                result=${record}
                emptyHint="Fill in the service details to preview the leak
                    rate and download the record."
            >
                <form class="tool-form" onSubmit=${handleSubmit}>

                <div class="tool-form-section">
                    <p class="tool-section-label">Company</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-company-name">
                                Company Name *
                            </label>
                            <input
                                id="rlg-company-name"
                                class="tool-input"
                                type="text"
                                required
                                value=${companyName.value}
                                onInput=${(e:Event) => {
                                    companyName.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-tech-name">
                                Technician Name *
                            </label>
                            <input
                                id="rlg-tech-name"
                                class="tool-input"
                                type="text"
                                required
                                value=${technicianName.value}
                                onInput=${(e:Event) => {
                                    technicianName.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-epa-cert">
                                EPA Cert # (optional)
                            </label>
                            <input
                                id="rlg-epa-cert"
                                class="tool-input"
                                type="text"
                                value=${epaCertNumber.value}
                                onInput=${(e:Event) => {
                                    epaCertNumber.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-branch">
                                Branch (optional)
                            </label>
                            <input
                                id="rlg-branch"
                                class="tool-input"
                                type="text"
                                value=${branch.value}
                                onInput=${(e:Event) => {
                                    branch.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-phone">
                                Phone (optional)
                            </label>
                            <input
                                id="rlg-phone"
                                class="tool-input"
                                type="tel"
                                value=${phone.value}
                                onInput=${(e:Event) => {
                                    phone.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-email">
                                Email (optional)
                            </label>
                            <input
                                id="rlg-email"
                                class="tool-input"
                                type="email"
                                value=${email.value}
                                onInput=${(e:Event) => {
                                    email.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Customer / Site</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-customer-name">
                                Customer Name *
                            </label>
                            <input
                                id="rlg-customer-name"
                                class="tool-input"
                                type="text"
                                required
                                value=${customerName.value}
                                onInput=${(e:Event) => {
                                    customerName.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-site-name">
                                Site Name (optional)
                            </label>
                            <input
                                id="rlg-site-name"
                                class="tool-input"
                                type="text"
                                value=${siteName.value}
                                onInput=${(e:Event) => {
                                    siteName.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-address">
                                Address (optional)
                            </label>
                            <input
                                id="rlg-address"
                                class="tool-input"
                                type="text"
                                value=${address.value}
                                onInput=${(e:Event) => {
                                    address.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-equip-location">
                                Equipment Location (optional)
                            </label>
                            <input
                                id="rlg-equip-location"
                                class="tool-input"
                                type="text"
                                value=${equipmentLocation.value}
                                onInput=${(e:Event) => {
                                    equipmentLocation.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-equip-id">
                                Equipment ID (optional)
                            </label>
                            <input
                                id="rlg-equip-id"
                                class="tool-input"
                                type="text"
                                value=${equipmentId.value}
                                onInput=${(e:Event) => {
                                    equipmentId.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Refrigerant</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-refrigerant-type">
                                Refrigerant Type *
                            </label>
                            <select
                                id="rlg-refrigerant-type"
                                class="tool-select"
                                required
                                value=${refrigerantType.value}
                                onChange=${(e:Event) => {
                                    refrigerantType.value =
                                        (e.target as HTMLSelectElement).value
                                }}
                            >
                                ${REFRIGERANT_TYPES.map(r => html`
                                    <option
                                        key=${r}
                                        value=${r}
                                        selected=${
                                            refrigerantType.value === r
                                        }
                                    >${r}</option>
                                `)}
                            </select>
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-added-lbs">
                                Refrigerant Added (lbs) *
                            </label>
                            <input
                                id="rlg-added-lbs"
                                class="tool-input"
                                type="number"
                                required
                                min="0"
                                step="0.1"
                                value=${addedLbs.value}
                                onInput=${(e:Event) => {
                                    addedLbs.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-recovered-lbs">
                                Refrigerant Recovered (lbs, optional)
                            </label>
                            <input
                                id="rlg-recovered-lbs"
                                class="tool-input"
                                type="number"
                                min="0"
                                step="0.1"
                                value=${recoveredLbs.value}
                                onInput=${(e:Event) => {
                                    recoveredLbs.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-full-charge">
                                Full System Charge (lbs) *
                            </label>
                            <input
                                id="rlg-full-charge"
                                class="tool-input"
                                type="number"
                                required
                                min="0.1"
                                step="0.1"
                                value=${fullChargeLbs.value}
                                onInput=${(e:Event) => {
                                    fullChargeLbs.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Leak Rate</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-date-current">
                                Date of Current Service *
                            </label>
                            <input
                                id="rlg-date-current"
                                class="tool-input"
                                type="date"
                                required
                                value=${dateCurrentService.value}
                                onInput=${(e:Event) => {
                                    dateCurrentService.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-date-previous">
                                Date of Previous Refrigerant Addition *
                            </label>
                            <input
                                id="rlg-date-previous"
                                class="tool-input"
                                type="date"
                                required
                                value=${datePreviousAddition.value}
                                onInput=${(e:Event) => {
                                    datePreviousAddition.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-appliance-type">
                                Appliance Type *
                            </label>
                            <select
                                id="rlg-appliance-type"
                                class="tool-select"
                                required
                                value=${applianceType.value}
                                onChange=${(e:Event) => {
                                    applianceType.value =
                                        (e.target as HTMLSelectElement)
                                            .value as ApplianceType
                                }}
                            >
                                ${(Object.entries(APPLIANCE_LABELS) as
                                    [ApplianceType, string][]
                                ).map(([val, label]) => html`
                                    <option
                                        key=${val}
                                        value=${val}
                                        selected=${
                                            applianceType.value === val
                                        }
                                    >${label}</option>
                                `)}
                            </select>
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Service Notes</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-leak-location">
                                Leak Location (optional)
                            </label>
                            <input
                                id="rlg-leak-location"
                                class="tool-input"
                                type="text"
                                value=${leakLocation.value}
                                onInput=${(e:Event) => {
                                    leakLocation.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-repairs">
                                Repairs Performed (optional)
                            </label>
                            <textarea
                                id="rlg-repairs"
                                class="tool-textarea"
                                value=${repairsPerformed.value}
                                onInput=${(e:Event) => {
                                    repairsPerformed.value =
                                        (e.target as HTMLTextAreaElement)
                                            .value
                                }}
                            ></textarea>
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-components">
                                Components Replaced (optional)
                            </label>
                            <input
                                id="rlg-components"
                                class="tool-input"
                                type="text"
                                value=${componentsReplaced.value}
                                onInput=${(e:Event) => {
                                    componentsReplaced.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-follow-up">
                                Follow-up Required (optional)
                            </label>
                            <input
                                id="rlg-follow-up"
                                class="tool-input"
                                type="text"
                                value=${followUp.value}
                                onInput=${(e:Event) => {
                                    followUp.value =
                                        (e.target as HTMLInputElement).value
                                }}
                            />
                        </div>
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="rlg-notes">
                                Additional Notes (optional)
                            </label>
                            <textarea
                                id="rlg-notes"
                                class="tool-textarea"
                                value=${additionalNotes.value}
                                onInput=${(e:Event) => {
                                    additionalNotes.value =
                                        (e.target as HTMLTextAreaElement)
                                            .value
                                }}
                            ></textarea>
                        </div>
                    </div>
                </div>

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Preview ${'&'} Download PDF
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${handleReset}
                    >
                        Reset
                    </substrate-button>
                </div>
                </form>
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
